'use client';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import {
  useSaveOptOutOfGroup,
  useSaveRemoveOfGroup,
} from '@/modules/league/new-components/groups-components/hooks';
import ConfirmationModal from '@/modules/league/new-components/header/ConfirmationModal';
import {
  GroupPlayer,
  LeagueGameDayHeaderDetails,
  MatchGroup,
} from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import { getStatsTableForGroup } from '@/utils/actions/leagues/gameDay/functions/getStatsTableForGroupCard';
import { getNumberSuffix } from '@/utils/helpers/getNumberSuffix';

import { useQueryClient } from '@tanstack/react-query';
import ChangeScoreFormatModal from '@/modules/league/new-components/change-score-format-components/ChangeFormatModal';
import { toast } from 'react-toastify';

interface GroupsCardProps {
  players: GroupPlayer[] | undefined;
  groupNumber: number | undefined;
  groupName: string | undefined;
  groupSize: number | undefined;
  remainingGames: number | undefined;
  gameDaySession: LeagueGameDayHeaderDetails;
  gameDayInfo: LeagueGameDayHeaderDetails;
  isFirstGroup: boolean;
  isLastGroup: boolean;
  changeOrderInGroup: (fromIndex: number, toIndex: number) => void;
  matchDateId: string;
  matchesStarted: boolean;
  playerType: string | undefined;
  matchGroups: MatchGroup[];
  isFlexLeague: boolean | undefined;
  isAuthorized: boolean | null;
  userUuid: string | undefined;
  isRatingLeague: boolean;
  isBench: boolean;
  poolId: string;
}

export const GroupCard = (props: GroupsCardProps) => {
  const {
    players,
    groupNumber,
    remainingGames,
    groupName,
    groupSize,
    gameDaySession,
    gameDayInfo,
    isFirstGroup,
    isLastGroup,
    changeOrderInGroup,
    playerType,
    matchDateId,
    matchesStarted,
    isFlexLeague,
    isAuthorized,
    userUuid,
    isRatingLeague,
    isBench,
    poolId,
  } = props;
  const queryClient = useQueryClient();
  const matchingPlayer = userUuid ? players?.find((player) => player.UserID_1 === userUuid) : '';
  let IsTheSameGroup = '';
  if (matchingPlayer) {
    IsTheSameGroup = matchingPlayer.PoolID;
  } else {
    IsTheSameGroup = '';
  }
  const [selectedPlayer, setSelectedPlayer] = useState({
    firstName: '',
    lastName: '',
    userId: '',
  });
  const [optOutSelection, setOptOutSelection] = useState({} as GroupPlayer);

  const [showOptOut, setShowOptOut] = useState(false);
  const [showDoupleConfirmOptOutSinglePlayer, setShowDoupleConfirmOptOutSinglePlayer] =
    useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [isChangeScoreFormatModalOpened, setIsChangeScoreFormatModalOpened] = useState(false);

  const [selectedPlayerPartner, setSelectedPlayerPartner] = useState({
    firstName: '',
    lastName: '',
  });

  const { mutate: saveOptOutOfGroup } = useSaveOptOutOfGroup();
  const { mutate: saveRemovePlayerOfGroup } = useSaveRemoveOfGroup();
  const { setLoading } = useLoader();

  return (
    <>
      <div className='mb-3 h-auto w-full gap-12 rounded-lg border bg-white px-2 py-3 shadow-sm md:p-4'>
        <div className='items-left flex flex-col justify-between gap-1 md:mb-4 md:h-[30px] md:flex-row md:items-center'>
          <div className='flex justify-between md:hidden'>
            <h3 className='flex text-base font-semibold'>
              {isRatingLeague ? 'Court' : 'Group'} {groupNumber}
            </h3>
            <p className='text-right text-sm text-gray-500'>{groupSize} players</p>
          </div>
          <div className='flex justify-between pb-2 md:hidden'>
            <p
              className={`text-right text-sm ${matchesStarted || !isAuthorized ? 'text-gray-500 ' : 'text-leagues-500/90 cursor-pointer'}`}
              onClick={() => {
                if (!matchesStarted && isAuthorized) {
                  setIsChangeScoreFormatModalOpened(true);
                }
              }}
            >
              {groupName}
            </p>
            {!isRatingLeague && (
              <p className='text-sm text-gray-500'>{`${remainingGames} matches remaining`}</p>
            )}
          </div>
          <h3 className='hidden text-lg font-semibold md:flex'>
            {isRatingLeague ? 'Court' : 'Group'} {groupNumber}
          </h3>
          <div className='hidden justify-between gap-4 md:flex'>
            {!isRatingLeague && (
              <>
                <p className='text-sm text-gray-500'>{`${remainingGames} matches remaining`}</p>
                <p>|</p>
              </>
            )}

            <p
              className={`text-sm ${matchesStarted || !isAuthorized ? 'text-gray-500 ' : 'text-leagues-500/90 cursor-pointer'}`}
              onClick={() => {
                if (!matchesStarted && isAuthorized) {
                  setIsChangeScoreFormatModalOpened(true);
                }
              }}
            >
              {groupName}
            </p>
            <p>|</p>
            <p className='text-sm text-gray-500'>{groupSize} players</p>
          </div>
        </div>
        <div className='flex w-full flex-col gap-3'>
          {players
            ? players?.map((player, i) => {
                const isUserAlsoPlayer = userUuid === player.UserID_1;
                return (
                  <div
                    key={`player-${i}`}
                    className={`w-full ${gameDayInfo.isMatchStarted ? (isUserAlsoPlayer ? 'rounded-lg border-2 border-leagues-300/50 p-3 hover:border-leagues-300/90 md:p-4' : 'rounded-lg border-2 p-3 hover:border-leagues-300/10 md:p-4') : ''}`}
                  >
                    <div className={`flex items-center gap-2`}>
                      <div className='flex w-full flex-row gap-2 md:gap-4'>
                        {matchesStarted || !isAuthorized || isFlexLeague ? null : (
                          <div
                            className={`flex w-full max-w-16 items-center ${(i === 0 && groupNumber === 1) || (i === players.length - 1 && isLastGroup && !isRatingLeague && !isBench) ? 'justify-end' : 'justify-between'} bg-white text-gray-900 md:max-w-16`}
                          >
                            {!(
                              isFirstGroup &&
                              player.PoolSeedNumber === 1 &&
                              player.CurrentSeedNumber === 1
                            ) ||
                            !(
                              isLastGroup &&
                              player.PoolSeedNumber === 1 &&
                              player.CurrentSeedNumber === 1
                            ) ? (
                              isFirstGroup &&
                              ((player.PoolSeedNumber === 1 && player.CurrentSeedNumber === 1) ||
                                (player.PoolSeedNumber === 1 &&
                                  player.TeamNewSeedNumber === 1)) ? null : (
                                <FontAwesomeIcon
                                  onClick={() =>
                                    changeOrderInGroup(
                                      player.CurrentSeedNumber - 1,
                                      player.CurrentSeedNumber - 2
                                    )
                                  }
                                  icon={faArrowUp}
                                  className='flex size-3 items-center justify-center rounded-full border-2 border-leagues-700 bg-white p-1 text-sm text-leagues-700 hover:cursor-pointer hover:bg-leagues-500 hover:text-white md:text-base'
                                />
                              )
                            ) : null}

                            {!(isLastGroup && i === players.length - 1) ||
                            (isRatingLeague && isBench) ? (
                              <FontAwesomeIcon
                                onClick={() =>
                                  changeOrderInGroup(
                                    player.CurrentSeedNumber - 1,
                                    player.CurrentSeedNumber
                                  )
                                }
                                icon={faArrowDown}
                                className='flex size-3 items-center justify-center rounded-full border-2 border-leagues-700 bg-white p-1 text-sm text-leagues-700 hover:cursor-pointer hover:bg-leagues-500 hover:text-white md:text-base'
                              />
                            ) : null}
                          </div>
                        )}

                        <div
                          className={`flex w-full flex-col gap-3 rounded-md hover:bg-gray-50 ${!gameDayInfo.isMatchStarted ? (isUserAlsoPlayer ? 'border border-leagues-300/50 p-3 shadow-sm' : 'border p-3 shadow-sm') : 'pb-2'}`}
                        >
                          <div className='flex w-full flex-row justify-between font-semibold'>
                            <div
                              className={`flex items-center text-xs font-medium text-leagues-500 ${!gameDayInfo.isMatchStarted ? 'justify-start italic' : 'size-[25px] justify-center rounded-full bg-leagues-300/20 p-2'}`}
                            >
                              {gameDayInfo.isMatchStarted
                                ? player.PoolSeedNumber
                                : `${player.TeamNewSeedNumber}${getNumberSuffix(player.TeamNewSeedNumber)} place - ${player.StatsSeedOnGameDay}${getNumberSuffix(player.StatsSeedOnGameDay)} seed`}
                            </div>
                            {(playerType === 'Partner' && players.length > 2) ||
                            players.length > 2 ||
                            players.length > 4 ? (
                              <>
                                {!matchesStarted && isAuthorized ? (
                                  <p
                                    className={`cursor-pointer text-sm font-semibold text-leagues-700 hover:text-leagues-500 ${!gameDayInfo.isMatchStarted ? 'justify-end' : 'size-[25px] justify-center rounded-full bg-leagues-300/90 p-2'}`}
                                    onClick={() => {
                                      setShowOptOut(true);
                                      setSelectedPlayer({
                                        firstName: player.FirstName_1,
                                        lastName: player.LastName_1,
                                        userId: player.UserID_1,
                                      });
                                      if (player.FirstName_2 !== '') {
                                        setSelectedPlayerPartner({
                                          firstName: player.FirstName_2,
                                          lastName: player.LastName_2,
                                        });
                                      }
                                      setOptOutSelection(player);
                                    }}
                                  >
                                    Opt out
                                  </p>
                                ) : null}
                              </>
                            ) : null}

                            {(playerType === 'Partner' && players.length > 2) ||
                            players.length > 4 ? (
                              <>
                                {matchesStarted &&
                                (isAuthorized ||
                                  (isFlexLeague && userUuid && IsTheSameGroup === player.PoolID)) &&
                                !(player.TotalGamesAlreadyPlayed > 0) ? (
                                  <p
                                    className='cursor-pointer pr-2 text-sm font-semibold text-leagues-700 hover:text-leagues-500'
                                    onClick={() => {
                                      setShowRemove(true);
                                      setSelectedPlayer({
                                        firstName: player.FirstName_1,
                                        lastName: player.LastName_1,
                                        userId: player.UserID_1,
                                      });
                                      if (player.FirstName_2 !== '') {
                                        setSelectedPlayerPartner({
                                          firstName: player.FirstName_2,
                                          lastName: player.LastName_2,
                                        });
                                      }
                                      setOptOutSelection(player);
                                    }}
                                  >
                                    Remove
                                  </p>
                                ) : null}
                              </>
                            ) : null}
                          </div>

                          {!isAuthorized && (
                            <p
                              className={`font-semibold ${!gameDayInfo.isMatchStarted ? 'pb-1' : ''}`}
                            >
                              {player.FirstName_1} {player.LastName_1}
                            </p>
                          )}
                          {!isAuthorized &&
                          player.FirstName_2 !== '' &&
                          player.LastName_2 !== null ? (
                            <p
                              className={`font-semibold ${!gameDayInfo.isMatchStarted ? 'pb-1' : ''}`}
                            >
                              {player.FirstName_2} {player.LastName_2}
                            </p>
                          ) : null}
                          {isAuthorized && (
                            <div className={`flex w-full flex-row justify-between font-semibold`}>
                              <div className='flex flex-col text-gray-900'>
                                <p className={`line-clamp-3`}>
                                  {player.FirstName_1} {player.LastName_1}
                                </p>
                                {player.FirstName_2 !== '' && player.LastName_2 !== null ? (
                                  <p
                                    className={`${!gameDayInfo.isMatchStarted ? 'line-clamp-3' : ''}`}
                                  >
                                    {player.FirstName_2} {player.LastName_2}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          )}
                          {gameDayInfo.isMatchStarted && (
                            <p className='flex text-sm text-gray-700'>
                              League: {player.StatsSeedOnGameDay}
                              {getNumberSuffix(player.StatsSeedOnGameDay)} seed
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    {gameDayInfo.isMatchStarted
                      ? getStatsTableForGroup(player, gameDaySession.rankingType)
                      : null}
                  </div>
                );
              })
            : null}
        </div>
      </div>
      <ConfirmationModal
        open={showOptOut}
        onClose={() => setShowOptOut(false)}
        onConfirm={() => {
          setShowDoupleConfirmOptOutSinglePlayer(true);
          setShowOptOut(false);
        }}
        title='Are you sure?'
        message={`This will opt out ${selectedPlayer.firstName} ${selectedPlayer.lastName} ${
          playerType === 'Partner' &&
          selectedPlayerPartner &&
          `and ${selectedPlayerPartner.firstName} ${selectedPlayerPartner.lastName}`
        }`}
        confirmButtonText='Yes'
      />
      <ConfirmationModal
        open={showDoupleConfirmOptOutSinglePlayer}
        onClose={() => setShowDoupleConfirmOptOutSinglePlayer(false)}
        onConfirm={() => {
          setLoading(true);
          saveOptOutOfGroup(
            { matchDateId: matchDateId, player: optOutSelection },
            {
              onSuccess: (data) => {
                if (data.message) {
                  toast.success(data.message);
                }
                queryClient.invalidateQueries(['league_groups']);
                setLoading(false);
              },
              onError: () => {
                toast.error('Failed to opt out the player from the group');
                setLoading(false);
              },
            }
          );
          setShowDoupleConfirmOptOutSinglePlayer(false);
        }}
        title='Are you sure?'
        message={`This will opt out ${selectedPlayer.firstName} ${selectedPlayer.lastName} ${
          playerType === 'Partner' &&
          selectedPlayerPartner &&
          `and ${selectedPlayerPartner.firstName} ${selectedPlayerPartner.lastName}`
        } from this game day`}
        confirmButtonText='Yes'
      />
      <ConfirmationModal
        open={showRemove}
        onClose={() => setShowRemove(false)}
        onConfirm={() => {
          setLoading(true);
          saveRemovePlayerOfGroup(
            { matchDateId: matchDateId, player: optOutSelection },
            {
              onSuccess: (data) => {
                if (data.message) {
                  toast.success(data.message);
                }
                queryClient.invalidateQueries(['league_groups']);
                setLoading(false);
              },
              onError: () => {
                toast.error('Failed to remove the player from the group');
                setLoading(false);
              },
            }
          );
          setShowRemove(false);
        }}
        title='Are you sure?'
        message={`This will remove ${selectedPlayer.firstName} ${selectedPlayer.lastName} ${
          playerType === 'Partner' &&
          selectedPlayerPartner &&
          `and ${selectedPlayerPartner.firstName} ${selectedPlayerPartner.lastName}`
        }`}
        confirmButtonText='Yes'
      />
      {isChangeScoreFormatModalOpened && (
        <ChangeScoreFormatModal
          openModal={isChangeScoreFormatModalOpened}
          setIsModalOpen={setIsChangeScoreFormatModalOpened}
          title={`${isRatingLeague ? 'Court' : 'Group'} ${groupNumber}`}
          currentScoreFormat={groupName || ''}
          isMatchesPage={false}
          matchDateId={matchDateId}
          poolId={poolId}
          scoreId=''
        />
      )}
    </>
  );
};
